import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, User, LogIn, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/auth/AuthModal";

const links = [
  { href: "/#services", label: "Services", testId: "nav-link-services" },
  { href: "/portfolio", label: "Portfolio", testId: "nav-link-portfolio" },
  { href: "/#process", label: "Process", testId: "nav-link-process" },
  {
    href: "mailto:hello@dmvfp.com?subject=Question%20about%20DMV%20Floor%20Plans",
    label: "Contact",
    testId: "nav-link-contact",
    external: true,
  },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [freebies, setFreebies] = useState(0);
  const { user, authHeader, API } = useAuth();
  const location = useLocation();
  // On dark-hero pages like /rlah, before scroll, invert nav text to white.
  const lightMode = location.pathname === "/rlah";
  const light = lightMode && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!user?.is_admin) {
      setFreebies(0);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/stats`, {
          headers: authHeader(),
          withCredentials: true,
        });
        if (!cancelled) setFreebies(data.freebies_pending || 0);
      } catch (err) {
        console.error("nav freebies poll failed:", err);
      }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [user, API, authHeader]);

  const startAuth = (mode) => {
    setAuthMode(mode);
    setAuthOpen(true);
    setOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "backdrop-blur-xl bg-[#FAF7F2]/85 border-b border-stone-200"
            : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-12 h-20 flex items-center justify-between">
          <a
            href="/#top"
            className="flex flex-col items-start group leading-tight"
            data-testid="brand-link"
          >
            <span className="flex items-center gap-1">
              <img
                src="/brand-icon.png"
                alt=""
                aria-hidden="true"
                className={`h-10 w-10 object-contain transition-transform group-hover:rotate-6 ${light ? "brightness-0 invert" : ""}`}
              />
              <span className={`font-display font-semibold tracking-tight text-4xl ${light ? "text-white" : "text-stone-900"}`}>
                DMV
              </span>
            </span>
            <span className={`font-display font-semibold tracking-tight text-2xl mt-0.5 ${light ? "text-white/90" : "text-stone-700"}`}>
              Floor Plans
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.testId}
                className={`text-sm font-medium px-4 py-2 rounded-full transition-colors ${
                  light
                    ? "text-white/85 hover:text-white hover:bg-white/10"
                    : "text-stone-700 hover:text-stone-900 hover:bg-stone-100"
                }`}
              >
                {l.label}
              </a>
            ))}

            {user ? (
              <Link
                to="/account"
                data-testid="nav-account-link"
                className="ml-2 inline-flex items-center gap-2 bg-white border border-stone-200 text-stone-800 text-sm font-semibold px-4 py-2 rounded-full hover:border-teal-700 hover:text-teal-700 transition-colors"
              >
                <User className="h-3.5 w-3.5" /> {user.name?.split(" ")[0] || "Account"}
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => startAuth("login")}
                data-testid="nav-signin-btn"
                className="ml-2 inline-flex items-center gap-2 bg-white border border-stone-200 text-stone-800 text-sm font-semibold px-4 py-2 rounded-full hover:border-teal-700 hover:text-teal-700 transition-colors"
              >
                <LogIn className="h-3.5 w-3.5" /> Sign in
              </button>
            )}

            {user?.is_admin && (
              <Link
                to="/admin"
                data-testid="nav-admin-link"
                className="ml-2 relative inline-flex items-center gap-2 bg-teal-700 text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-stone-900 transition-colors"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Admin
                {freebies > 0 && (
                  <span
                    data-testid="nav-admin-badge"
                    className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#FAF7F2]"
                  >
                    {freebies}
                  </span>
                )}
              </Link>
            )}

            <a
              href="/#contact"
              data-testid="nav-cta-quote"
              className="ml-1 inline-flex items-center gap-2 bg-stone-900 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-teal-700 transition-colors"
            >
              Request a Quote
              <span aria-hidden>→</span>
            </a>
          </nav>

          <button
            className="md:hidden h-11 w-11 rounded-full border border-stone-300 bg-white flex items-center justify-center"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div
            className="md:hidden border-t border-stone-200 bg-white"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-2">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${l.testId}`}
                  className="text-sm font-medium text-stone-800 py-2 px-3 rounded-xl hover:bg-stone-50"
                >
                  {l.label}
                </a>
              ))}
              {user ? (
                <Link
                  to="/account"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-account-link"
                  className="text-sm font-medium text-stone-800 py-2 px-3 rounded-xl hover:bg-stone-50 inline-flex items-center gap-2"
                >
                  <User className="h-4 w-4" /> {user.name?.split(" ")[0] || "Account"}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => startAuth("login")}
                  data-testid="mobile-nav-signin-btn"
                  className="text-left text-sm font-medium text-stone-800 py-2 px-3 rounded-xl hover:bg-stone-50 inline-flex items-center gap-2"
                >
                  <LogIn className="h-4 w-4" /> Sign in
                </button>
              )}
              {user?.is_admin && (
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-admin-link"
                  className="text-sm font-semibold text-teal-700 py-2 px-3 rounded-xl hover:bg-teal-50 inline-flex items-center gap-2"
                >
                  <ShieldCheck className="h-4 w-4" /> Admin
                </Link>
              )}
              <a
                href="/#contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta-quote"
                className="mt-2 bg-stone-900 text-white text-sm font-semibold px-5 py-3 rounded-full text-center"
              >
                Request a Quote
              </a>
            </div>
          </div>
        )}
      </header>

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        initialMode={authMode}
        key={`${authOpen}-${authMode}-${location.pathname}`}
      />
    </>
  );
}
